import React from "react";
import {
  Box,
  Typography,
  Divider,
  Link,
  Stack,
  Card,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useLocation } from "react-router-dom";
import orderThatIconWhite from "../Images/orderThatIconWhite.png";
import TermsSection from "./components/TermsSection";
import { useThemeColors } from "../Context/Theme";

const sections = [
  {
    title: "1. Information We Collect",
    items: ["1.1 Information You Provide:"],
  },
  {
    nested: true,
    items: [
      "Account Registration: We collect personal information, such as your name, email address, phone number, and payment details, when you create an account or place an order.",
      "Order Details: Information related to your purchases, including product selections, quantities, and additional pickup person details.",
    ],
  },
  {
    items: ["1.2 Automatically Collected Information:"],
  },
  {
    nested: true,
    items: [
      "Device Information: We collect technical details about your device, including IP address, operating system, browser type, and device identifiers.",
      "Usage Data: Information about your interactions with the App, such as pages viewed, time spent on the App, and navigation paths.",
    ],
  },
  {
    items: ["1.3 Location Data:"],
  },
  {
    nested: true,
    items: [
      "With your consent, we may collect precise geolocation data to assist with order pickups or optimize App functionality.",
    ],
  },
  {
    items: ["1.4 Third-Party Information:"],
  },
  {
    nested: true,
    items: [
      "We may receive additional information from third-party service providers, such as payment processors and analytics services.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    items: ["2.1 To Provide Services:"],
  },
  {
    nested: true,
    items: [
      "Process and fulfill your orders, including generating QR codes for order retrieval.",
      "Facilitate order pickups at designated locations.",
    ],
  },
  {
    items: ["2.2 To Enhance User Experience:"],
  },
  {
    nested: true,
    items: [
      "Analyze usage trends and user preferences to personalize App functionality and recommendations.",
      "Develop new features, products, and services tailored to user needs.",
    ],
  },
  {
    items: ["2.3 For Marketing and Promotions:"],
  },
  {
    nested: true,
    items: [
      "Send promotional communications, special offers, and advertisements tailored to your preferences.",
      "Use aggregated or anonymized data to conduct market research and improve business strategies.",
    ],
  },
  {
    items: ["2.4 For Business Operations:"],
  },
  {
    nested: true,
    items: [
      "Conduct analytics and monitor performance to enhance operational efficiency.",
      "Develop partnerships and collaborations informed by user behavior and preferences.",
    ],
  },
  {
    items: ["2.5 For Broader Applications:"],
  },
  {
    nested: true,
    items: [
      "Use data for internal research, artificial intelligence training, and predictive modeling to improve user experience and optimize service offerings.",
      "Share anonymized or aggregated data with partners, advertisers, and other third parties to demonstrate App performance and user engagement.",
    ],
  },
  {
    items: ["2.6 For Legal Compliance:"],
  },
  {
    nested: true,
    items: [
      "Comply with applicable laws, regulations, and legal processes.",
      "Enforce our Terms and Conditions and other agreements.",
    ],
  },
  {
    title: "3. Sharing Your Information",
    items: ["3.1 With Service Providers:"],
  },
  {
    nested: true,
    items: [
      "We share your information with trusted third-party vendors who assist with payment processing, order fulfillment, marketing campaigns, and App optimization.",
    ],
  },
  {
    items: ["3.2 For Strategic Partnerships:"],
  },
  {
    nested: true,
    items: [
      "We may share data with partner organizations to deliver enhanced or complementary services. User data may be utilized to develop joint marketing initiatives and targeted campaigns.",
    ],
  },
  {
    items: ["3.3 For Legal Reasons:"],
  },
  {
    nested: true,
    items: [
      "We may disclose your information to comply with legal obligations, protect our rights, or respond to lawful requests.",
    ],
  },
  {
    items: ["3.4 Business Transfers:"],
  },
  {
    nested: true,
    items: [
      "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.",
    ],
  },
  {
    title: "4. Data Retention",
    items: [
      "We retain your personal information for as long as necessary to fulfill the purposes outlined in this Policy, comply with legal obligations, resolve disputes, and enforce our agreements. We may also retain aggregated or anonymized data indefinitely for analytics, research, or business development purposes.",
    ],
  },
  {
    title: "5. Your Privacy Choices",
    items: ["5.1 Account Settings:"],
  },
  {
    nested: true,
    items: [
      "You can update your account information or close your account at any time through the App’s settings.",
    ],
  },
  {
    items: ["5.2 Access and Correction:"],
  },
  {
    nested: true,
    items: [
      "You have the right to access and correct inaccuracies in your personal data by contacting us at hello@orderthat.com.",
    ],
  },
  {
    items: ["5.3 Opt-Out:"],
  },
  {
    nested: true,
    items: [
      "You may opt out of promotional communications by following the unsubscribe instructions in our emails.",
    ],
  },
  {
    items: ["5.4 Location Data:"],
  },
  {
    nested: true,
    items: [
      "Disable location services in your device settings to stop sharing geolocation data with us. Note that this may disable certain features of the application which require geolocation to function.",
    ],
  },
  {
    title: "6. Data Security",
    items: [
      "We implement appropriate technical and organizational measures to protect your personal data from unauthorized access, use, or disclosure. However, no security measures are infallible, and we cannot guarantee the absolute security of your data.",
    ],
  },
  {
    title: "7. Children’s Privacy",
    items: [
      "The App is not intended for individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe a minor has provided us with their information, please contact us to delete it.",
    ],
  },
  {
    title: "8. California Privacy Rights",
    items: [
      "If you are a California resident, you have the right to request details about the personal information we collect and its uses. To exercise these rights, contact us at hello@orderthat.com.",
    ],
  },
  {
    title: "9. Changes to This Privacy Policy",
    items: [
      "We reserve the right to update this Policy at any time. Changes will be effective immediately upon posting in the App. Continued use of the App constitutes your acceptance of the revised Policy.",
    ],
  },
];

const PrivacyPolicy = () => {
  const {
    fontFamily,
    fontWeight,
    fontSize,
    fontFamilyHeading,
    fontWeightHeading,
    fontSizeHeading
  } = useThemeColors();
  const location = useLocation();

  const {isAtAllEvents} = location.state || {};

  const eventName = sessionStorage.getItem("eventName");

  const AtAllEvents = isAtAllEvents
  return (
    <>
      <Navbar isAtAllEvents={eventName === "privacy-policy" ? true : AtAllEvents} />
      <Stack
        width={"100%"}
        paddingTop={"20px"}
        paddingBottom={"20px"}
        sx={{
          backgroundColor: "#f9f9f9",
        }}
        direction="column"
        alignItems="center"
      >
        <Card
          elevation={0}
          sx={{
            padding: { xs: 2, sm: 4 },
            color: "#333",
            mt: "15vh",
            minHeight: "70vh",
            width: "70%",
            borderRadius: "32px",
            border: "1px solid #D9D9D9",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            <Divider
              sx={{
                flex: 1,
                height: "4px",
                bgcolor: "rgb(254, 59, 62)",
                opacity: 1,
              }}
            />
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{ textAlign: "center", color: "#333", fontSize: fontSizeHeading, fontWeight: fontWeightHeading, fontFamily: fontFamilyHeading }}
            >
              Privacy Policy
            </Typography>
            <Divider
              sx={{
                flex: 1,
                height: "4px",
                bgcolor: "rgb(254, 59, 62)",
                opacity: 1,
              }}
            />
          </Stack>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            gutterBottom
            sx={{ textAlign: "center", fontFamily }}
          >
            (Last Updated: December 19, 2024)
          </Typography>
          <Divider sx={{ marginY: 3, height: "2px", bgcolor: "#D9D9D9" }} />
          <span
            style={{
              color: "rgb(254, 59, 62)",
              fontWeight: "bold",
              textAlign: "center",
              width: "100%",
              display: "block",
              fontFamily,
              fontSize
            }}
          >
            Introduction
          </span>{" "}
          <Typography variant="body1" gutterBottom style={{fontFamily, fontWeight, fontSize}}>
            Order That ("we," "our," "us," or "Company") values your privacy and
            is committed to protecting your personal information. This Privacy
            Policy ("Policy") outlines how we collect, use, disclose, and
            safeguard your data when you use the OrderThat application (the
            "App") and related services. By accessing or using the App, you
            consent to the data practices described in this Policy.
            <span style={{ fontWeight: "bold" }}>
              If you do not agree to these practices, please discontinue use of
              the App immediately.
            </span>
          </Typography>
          <Divider sx={{ marginY: 3, height: "2px", bgcolor: "#D9D9D9" }} />
          {sections.map((section, index) => (
            <TermsSection
              key={index}
              title={section.title}
              items={section.items}
              nested={section.nested || false}
              isNestedStyleDisc={section.nested || false}
            />
          ))}
          <Typography variant="h6" sx={{ marginTop: 3, textAlign: "center", fontSize: fontSizeHeading, fontWeight: fontWeightHeading, fontFamily: fontFamilyHeading }}>
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "center" }} gutterBottom style={{ fontSize, fontWeight, fontFamily }}>
            For questions or concerns regarding this Privacy Policy, please
            contact us at{" "}
            <Link href="mailto:hello@orderthat.com">hello@orderthat.com</Link>.
          </Typography>
          <Divider sx={{ marginY: 3, height: "2px", bgcolor: "#D9D9D9" }} />
          <Typography variant="body2" color="textSecondary" textAlign="center" style={{fontSize, fontWeight, fontFamily}}>
            By using the OrderThat App, you acknowledge that you have read,
            understood, and agree to this Privacy Policy.
          </Typography>
        </Card>
      </Stack>
      <Footer isAtAllEvents={AtAllEvents} icon={orderThatIconWhite} />
    </>
  );
};

export default PrivacyPolicy;
